import { useQuery } from '@tanstack/react-query'
import { useId } from 'react'

import { api, unwrap } from '../api/client'
import { currentLanguage, t } from '../i18n'
import { ErrorNotice, Notice, Overlay, Pending } from '../ui'
import { Markdown } from './markdown'

/**
 * The `?` in the app bar, on every screen.
 *
 * Three rules from the help panel contract are visible here and are the reason
 * this is not just a fetch and a render:
 *
 * - **Order by rank, then by id.** The API already orders the panel, so the
 *   drawer renders the order it was given and never sorts again.
 * - **Fall back visibly.** A topic with no translation renders the English and
 *   says so; a stale translation renders with a warning. Neither is hidden,
 *   because a procedure somebody follows in the wrong language quietly is the
 *   failure the marker exists to prevent.
 * - **The route is the registered API route, prefix included** — the same
 *   string the router registers — because that is what the documentation build
 *   checks coverage against. Screens pass it; they do not pass their own URL.
 */

type PanelTopic = {
  id: string
  title: string
  body: string
  kind: string
  fallback: boolean
  stale: boolean
  steps?: { text: string; done_when?: string | null }[]
  ai_feature?: unknown
}

type Panel = { route: string; lang: string; topics: PanelTopic[] }

export function useHelpPanel(route: string, enabled: boolean) {
  const lang = currentLanguage()
  return useQuery({
    queryKey: ['help', 'panel', route, lang],
    enabled,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const answer = unwrap(
        await api.GET('/help/panel', { params: { query: { route, lang } } }),
      ) as unknown as Panel
      return answer
    },
  })
}

export function HelpDrawer({ route, onClose }: { route: string; onClose: () => void }) {
  const titleId = useId()
  const panel = useHelpPanel(route, true)

  return (
    <Overlay kind="drawer" titleId={titleId} onClose={onClose}>
      <header>
        <h2 id={titleId}>{t('help.title')}</h2>
        <button type="button" className="subtle" onClick={onClose}>
          {t('app.close')}
        </button>
      </header>
      <div className="body">
        <p className="muted mono">{route}</p>
        {panel.isPending ? <Pending /> : null}
        {panel.error ? <ErrorNotice error={panel.error} retry={() => void panel.refetch()} /> : null}
        {panel.data && panel.data.topics.length === 0 ? (
          <Notice tone="warn">{t('help.empty')}</Notice>
        ) : null}
        {panel.data?.topics.map((topic) => (
          <article className="help-topic" key={topic.id}>
            <h3>{topic.title}</h3>
            {topic.fallback ? <Notice tone="warn">{t('help.fallback')}</Notice> : null}
            {topic.stale ? <Notice tone="warn">{t('help.stale')}</Notice> : null}
            <Markdown body={topic.body} />
            {topic.steps && topic.steps.length > 0 ? (
              <>
                <h4>{t('help.steps')}</h4>
                <ol>
                  {topic.steps.map((step, at) => (
                    <li key={at}>{step.text}</li>
                  ))}
                </ol>
              </>
            ) : null}
            <p>
              <button
                type="button"
                className="subtle"
                onClick={() => {
                  // A deep link is `?help=<topic id>` on any screen, so a
                  // coordinator can paste a link to the exact procedure.
                  const url = new URL(window.location.href)
                  url.searchParams.set('help', topic.id)
                  void navigator.clipboard?.writeText(url.toString())
                }}
              >
                {t('help.deep_link')}
              </button>
            </p>
          </article>
        ))}
      </div>
    </Overlay>
  )
}
